import React from 'react';
import { useMachineDetail } from './presenter';
import { MachineDetailView } from './view';
import { Route } from '../../../components/layout/ProtectedLayout';
import { useMessageBox } from '../../../components/ui/MessageBox';
import { machinesApi } from '../../../lib/apis';

type Props = {
  id: string;
  navigate: (r: Route) => void;
};

const MachineViewPage: React.FC<Props> = ({ id, navigate }) => {
  const vm = useMachineDetail(id);
  const message = useMessageBox();
  const [generatingToken, setGeneratingToken] = React.useState(false);
  const [generatingVm, setGeneratingVm] = React.useState(false);
  const [syncing, setSyncing] = React.useState(false);
  return (
    <MachineDetailView
      data={vm.data}
      videos={vm.videos}
      loading={vm.loading}
      error={vm.error}
      productCounts={vm.productCounts}
      generatingToken={generatingToken}
      generatingVm={generatingVm}
      syncingVideos={syncing}
      onBack={() => navigate({ name: 'machines/index' })}
      onEdit={id => navigate({ name: 'machines/update', params: { id: String(id) } })}
      onAssignVideo={() => navigate({ name: 'machines/assign-videos', params: { id } })}
      onSyncVideos={async () => {
        setSyncing(true);
        try {
          await machinesApi.post(`/machines/${id}/sync-videos`, {});
          await message({ title: 'Sync started', message: 'Sync request sent to machine. It will download any missing videos.' });
        } catch (e: any) {
          await message({ title: 'Error', message: e.message || 'Failed to trigger sync' });
        } finally {
          setSyncing(false);
        }
      }}
      onGenerateToken={async () => {
        setGeneratingToken(true);
        try {
          const res = await machinesApi.post<{ data: { token: string; expiresIn: number } }>(`/machines/${id}/token`, {});
          const token = (res as any)?.data?.token || (res as any)?.token;
          const expires = (res as any)?.data?.expiresIn || (res as any)?.expiresIn;
          await message({
            title: 'Machine API Token',
            message: `Token:\n\n${token}\n\nExpires in: ${expires} seconds`,
            copyText: token,
            copyTextLabel: 'Copy token',
            okText: 'Close'
          });
        } catch (e: any) {
          await message({ title: 'Error', message: e.message || 'Failed to generate token' });
        } finally {
          setGeneratingToken(false);
        }
      }}
      onGenerateVm={async () => {
        setGeneratingVm(true);
        try {
          const base = (process.env.REACT_APP_API_BASE_MACHINES || process.env.REACT_APP_API_BASE || 'http://localhost:3002').toString();
          const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || '';
          const res = await fetch(`${base}/machines/${id}/source`, {
            method: 'GET',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          const disposition = res.headers.get('Content-Disposition') || res.headers.get('content-disposition');
          const fallbackName = `vending-machine-${id}.zip`;
          let filename = fallbackName;
          if (disposition) {
            const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
            const name = decodeURIComponent(match?.[1] || match?.[2] || '');
            if (name) filename = name;
          }
          if (!res.ok) {
            const text = await res.text();
            let errMsg = 'Failed to generate source zip';
            try { const j = text ? JSON.parse(text) : null; errMsg = j?.message || errMsg; } catch {}
            throw new Error(errMsg);
          }
          const blob = await res.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        } catch (e: any) {
          await message({ title: 'Error', message: e.message || 'Failed to generate VM source' });
        } finally {
          setGeneratingVm(false);
        }
      }}
    />
  );
};

export default MachineViewPage;
